import type { VoidFunctionComponent } from 'react';

import { SNIPPET_DETECTION } from '../../../../../shared/featureFlags/flagNames';
import { Action, ResourceType } from '../../../../../typings/permissions';
import { window } from '../../../../globals';
import { FeatureFlagRequired } from '../../../generic';
import Loader from '../../../generic/Loader';
import Header from '../../../shared/settings/SettingsPageHeader';
import { NetworkError } from '../../Errors';
import OrganizationSettingsPermissionGate from '../../OrganizationSettingsPermissionGate';
import { shouldDisplayOrganizationGeneralSettings } from '../../shared/permissions';

import ComponentsSection from './ComponentsSection';
import ContributorsSection from './ContributorsSection';
import GeneralSection from './GeneralSection';
import { useGeneralSettings } from './hooks';
import ProjectsSection from './ProjectsSection';
import SnippetsSection from './SnippetsSection';
import UsersSection from './UsersSection';

const shouldDisableGeneralSettings = !window.hasPermission({
  action: Action.Edit,
  resourceType: ResourceType.OrganizationGeneralSettings,
});

const shouldDisableUsersSection = !window.hasPermission({
  resourceType: ResourceType.Role,
  action: Action.Manage,
});

// User must have permission to view any project to receive a contributor report
const shouldDisableContributorsSection = !window.hasPermission({
  action: Action.View,
  resourceType: ResourceType.ProjectAny,
});

const SettingsOrganizationGeneral = () => {
  const { settings, initialSettings, isLoading, onUpdateSettings, isSaveButtonDisabled, isSaving, onSave, errors } =
    useGeneralSettings(window.user?.organizationId as number);

  if (isLoading) {
    return <Loader />;
  }

  // Settings should not be null unless there was an error
  if (!settings || !initialSettings) {
    return <NetworkError />;
  }

  return (
    <>
      <Header heading="Organization Settings">
        <Header.SaveButton isDisabled={isSaveButtonDisabled} onClick={onSave} isLoading={isSaving} />
      </Header>
      <GeneralSection
        settings={settings}
        onUpdateSettings={onUpdateSettings}
        isDisabled={shouldDisableGeneralSettings}
      />
      <UsersSection
        settings={settings}
        onUpdateSettings={onUpdateSettings}
        isDisabled={shouldDisableGeneralSettings || shouldDisableUsersSection}
        errors={errors}
      />
      <ProjectsSection
        settings={settings}
        initialSettings={initialSettings}
        isDisabled={shouldDisableGeneralSettings}
        // labels={settings.labels}
        onUpdateSettings={onUpdateSettings}
      />
      <ComponentsSection
        settings={settings}
        onUpdateSettings={onUpdateSettings}
        isDisabled={shouldDisableGeneralSettings}
      />
      <FeatureFlagRequired flagName={SNIPPET_DETECTION}>
        <SnippetsSection
          settings={settings}
          isDisabled={shouldDisableGeneralSettings}
          onUpdateSettings={onUpdateSettings}
          errors={errors}
        />
      </FeatureFlagRequired>
      <ContributorsSection isDisabled={shouldDisableContributorsSection} />
    </>
  );
};

/**
 * SettingsOrganizationGeneralContainer displays the organization general settings page if the user has permission to view it.
 */
const SettingsOrganizationGeneralContainer: VoidFunctionComponent = () => (
  <OrganizationSettingsPermissionGate checkPermission={shouldDisplayOrganizationGeneralSettings}>
    <SettingsOrganizationGeneral />
  </OrganizationSettingsPermissionGate>
);

export default SettingsOrganizationGeneralContainer;
